import { Receipt } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { formatPlanAmount } from '@/lib/builder/utils/payment-format';

interface PlatformFeeDueBannerProps {
    /** null quando le commissioni non riguardano chi guarda (il viaggiatore). */
    amount: number | null;
    /**
     * Su quanti incassi si e' accumulato. Omesso dove non lo si sa — le schede cliente e
     * consulente aggregano importi, non righe di `payments` — e in quel caso la seconda
     * riga non si disegna invece di stampare "0 incassi", che sarebbe falso.
     */
    count?: number | null;
}

/**
 * Commissioni Place2Be ancora da versare, in cima alla dashboard pagamenti.
 *
 * È il contraltare dell'aver riaperto "segna come pagata" a TD e agenzia: un incasso
 * dichiarato fuori Stripe non trattiene la fee, e senza un posto in cui quel debito si
 * accumula visibilmente resterebbe una riga in `payments` che nessuno guarda.
 *
 * Sta qui e non come quinta tile fra i KPI di proposito: quella griglia è 2x2/4-col e una
 * quinta casella la spezzerebbe, ma soprattutto questo non è un numero da monitorare come
 * gli altri — è una cosa da fare, e le cose da fare stanno sopra le tabelle.
 *
 * Non si disegna nulla se non c'è debito, e nemmeno se `amount` è null: il viaggiatore non
 * deve vedere che esiste una commissione fra l'agenzia e noi.
 */
export const PlatformFeeDueBanner = ({ amount, count }: PlatformFeeDueBannerProps) => {
    const { t } = useI18n();

    if (amount == null || amount <= 0) return null;

    return (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <span className="rounded-lg bg-amber-100 text-amber-600 p-2 flex-shrink-0">
                <Receipt className="w-4 h-4" />
            </span>
            <div className="flex flex-col leading-tight min-w-0">
                <span className="text-sm font-semibold text-amber-900">
                    {t('payments.platformFee.bannerTitle', { amount: formatPlanAmount(amount) })}
                </span>
                {count != null && (
                    <span className="text-sm text-amber-800 mt-0.5">
                        {t('payments.platformFee.bannerBody', { count })}
                    </span>
                )}
            </div>
        </div>
    );
};
